import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    role: 'customer'
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Mock credentials for different user types
  const mockCredentials = {
    customer: { email: 'customer@techtribes.ug', password: 'Customer123!' },
    admin: { email: 'admin@techtribes.ug', password: 'Admin123!' },
    partner: { email: 'partner@techtribes.ug', password: 'Partner123!' }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const expectedCredentials = mockCredentials?.[formData?.role];
      
      if (formData?.email === expectedCredentials?.email && formData?.password === expectedCredentials?.password) {
        const userData = {
          id: Math.random()?.toString(36)?.substr(2, 9),
          name: formData?.role === 'customer' ? 'John Mukasa' : formData?.role === 'admin' ? 'Sarah Nakato' : 'Peter Ssemakula',
          email: formData?.email,
          role: formData?.role,
          company: formData?.role === 'customer' ? 'Kampala Tech Solutions' : 'TechTribes Creations Ltd',
          phone: '+256 700 123 456',
          avatar: `https://randomuser.me/api/portraits/${formData?.role === 'admin' ? 'women' : 'men'}/32.jpg`
        };
        
        onLogin(userData);
        
        // Redirect based on role
        if (formData?.role === 'admin') {
          navigate('/admin-dashboard');
        } else {
          navigate('/customer-dashboard');
        }
      } else {
        setErrors({
          general: `Invalid credentials. Use ${expectedCredentials?.email} / ${expectedCredentials?.password}`
        });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  const roleOptions = [
    { value: 'customer', label: 'Customer' },
    { value: 'admin', label: 'Admin' },
    { value: 'partner', label: 'Partner' }
  ];

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-moderate p-8 border border-border">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-full mx-auto mb-4">
            <Icon name="Zap" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">Welcome Back</h1>
          <p className="text-muted-foreground">Sign in to your TechTribes account</p>
        </div>

        {errors?.general && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{errors?.general}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Account Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {roleOptions?.map((role) => (
                  <button
                    key={role?.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role: role?.value }))}
                    className={`px-3 py-2 text-sm font-medium rounded-lg border transition-smooth ${
                      formData?.role === role?.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                    }`}
                  >
                    {role?.label}
                  </button>
                ))}
              </div>
            </div>

            <Input
              label="Email Address"
              type="email"
              name="email"
              value={formData?.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              error={errors?.email}
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              value={formData?.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              error={errors?.password}
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label="Remember me"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
            />
            
            <button
              type="button"
              className="text-sm text-primary hover:text-primary/80 transition-smooth"
              onClick={() => {
                // In real app, this would open forgot password modal/page
                alert('Forgot password functionality would be implemented here');
              }}
            >
              Forgot password?
            </button>
          </div>

          <Button
            type="submit"
            variant="default"
            loading={isLoading}
            fullWidth
            iconName="LogIn"
            iconPosition="left"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-8 pt-6 border-t border-border text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Don't have an account?
          </p>
          <Button
            variant="outline"
            fullWidth
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => navigate('/register')}
          >
            Create Account
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;